import { useState, useEffect } from "react";
import "../styles/SignupModal.css";
import { toast } from 'react-toastify';

const SignupModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState("login"); 
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loginToken, setLoginToken] = useState("");

  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);

  // 🔹 Resend OTP state
  const [resendTimer, setResendTimer] = useState(0);
  const [isResending, setIsResending] = useState(false);

  // ✅ Hooks always before return
  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  if (!isOpen) return null;

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const validatePassword = (value) => value.length >= 6;

  const handleLogin = async (e) => {
    e.preventDefault();
    let valid = true;

    setEmailTouched(true);
    setPasswordTouched(true);
    setEmailError("");
    setPasswordError("");

    if (!email.trim()) {
      setEmailError("Please enter your email.");
      valid = false;
    } else if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address.");
      valid = false;
    }

    if (!password.trim()) {
      setPasswordError("Please enter your password.");
      valid = false;
    } else if (!validatePassword(password)) {
      setPasswordError("Password must be at least 6 characters.");
      valid = false;
    }

    if (!valid) return;

    try {
      const response = await fetch("https://digital.tifleducation.com/admin/public/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.response?.role_id === 1) {
          toast.error(data.error || "You are not authorized to login.");
          return;
        }
        toast.success("OTP sent successfully!");
        setTimeout(() => setStep("otp"), 1500);
      } else {
        toast.error(data.error || data.message || "Login failed.");
      }
    } catch (error) {
      toast.error("An error occurred during login.");
      console.error("Login Error:", error);
    }
  };

  const handleOtpChange = (index, value) => {
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < otp.length - 1) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").trim();
    if (/^\d+$/.test(pasteData)) {
      const pasteArray = pasteData.split("").slice(0, otp.length);
      const newOtp = [...otp];
      pasteArray.forEach((num, i) => (newOtp[i] = num));
      setOtp(newOtp);

      const lastIndex = pasteArray.length - 1;
      const focusIndex = lastIndex < otp.length ? lastIndex : otp.length - 1;
      const focusInput = document.getElementById(`otp-${focusIndex}`);
      if (focusInput) focusInput.focus();
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const otpCode = otp.join("");

    if (otpCode.length !== 6) {
      toast.warn("Please enter complete 6-digit OTP.");
      return;
    }

    try {
      const response = await fetch("https://digital.tifleducation.com/admin/public/api/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp: otpCode }),
      });

      const data = await response.json();

      if (response.ok) {
        const token = data.response?.token;
        if (token) {
          localStorage.setItem("token", token);
        } else {
          console.warn("Token missing in response");
        }
        toast.success("OTP verified and Login successfully!");

        setTimeout(() => {
          window.location.replace("/dashboard");
        }, 1000);
      } else {
        toast.error(data.message || "OTP verification failed.");
      }
    } catch (error) {
      toast.error("An error occurred during OTP verification.");
      console.error("OTP Error:", error);
    }
  };

  // 🔹 Resend OTP
  const handleResendOtp = async () => {
    if (resendTimer > 0 || isResending) return;

    setIsResending(true);
    try {
      const response = await fetch("https://digital.tifleducation.com/admin/public/api/resend-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("OTP resent successfully!");
        setResendTimer(60);
        setOtp(["", "", "", "", "", ""]);
        setTimeout(() => {
          const firstInput = document.getElementById("otp-0");
          if (firstInput) firstInput.focus();
        }, 300);
      } else {
        toast.error(data.message || "Failed to resend OTP.");
      }
    } catch (error) {
      toast.error("An error occurred while resending OTP.");
      console.error("Resend OTP Error:", error);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="modal-overlay2">
      <div className="modal-content2">
        <div className="modal-header2">
          <h3>{step === "login" ? "Teacher Login" : ""}</h3>
          <button className="close-btn" onClick={onClose}>
            &times;
          </button>
        </div>

        {step === "login" && (
          <form className="address-form" onSubmit={handleLogin}>
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              type="email"
              placeholder="Enter email address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailTouched) {
                  if (!e.target.value.trim()) {
                    setEmailError("Please enter your email.");
                  } else if (!validateEmail(e.target.value)) {
                    setEmailError("Please enter a valid email address.");
                  } else {
                    setEmailError("");
                  }
                }
              }}
              onBlur={() => {
                setEmailTouched(true);
                if (!email.trim()) {
                  setEmailError("Please enter your email.");
                } else if (!validateEmail(email)) {
                  setEmailError("Please enter a valid email address.");
                } else {
                  setEmailError("");
                }
              }}
            />
            {emailError && <p className="error-text1">{emailError}</p>}

            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              placeholder="Enter Password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordTouched) {
                  if (!e.target.value.trim()) {
                    setPasswordError("Please enter your password.");
                  } else if (!validatePassword(e.target.value)) {
                    setPasswordError("Password must be at least 6 characters.");
                  } else {
                    setPasswordError("");
                  }
                }
              }}
              onBlur={() => {
                setPasswordTouched(true);
                if (!password.trim()) {
                  setPasswordError("Please enter your password.");
                } else if (!validatePassword(password)) {
                  setPasswordError("Password must be at least 6 characters.");
                } else {
                  setPasswordError("");
                }
              }}
            />
            {passwordError && <p className="error-text1">{passwordError}</p>}

            <button type="submit" className="submit-btn">
              Login
            </button>
            <p className="admin-login-text">
              Are you an{" "}
              <a href="https://digital.tifleducation.com/admin/public/login">
                Admin?
              </a>
            </p>
          </form>
        )}

        {step === "otp" && (
          <form className="address-form" onSubmit={handleOtpSubmit}>
            <div className="verification">
              <h4 className="mb-2">Two Step Verification 💬</h4>
              {/* <p className="text-start">
                Enter the code from the email in the field below<br />
                <span className="fw-bold d-block">
                 admin@tifleducation.com
                </span>
              </p> */}
              <p className="fw-semibold">Type your 6 digit security code</p>
            </div>

            <div className="otp-boxes">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  onPaste={handleOtpPaste}
                  className="otp-input"
                />
              ))}
            </div>
            <button type="submit" className="submit-btn">
              Submit OTP
            </button>
            <div className="resend">
              {resendTimer > 0 && `(${resendTimer}s) `}Didn't get OTP?{" "}
              <span
                style={{
                  fontWeight: "500",
                  color: resendTimer > 0 ? "gray" : "#0e6b3d",
                  cursor: resendTimer > 0 ? "not-allowed" : "pointer",
                }}
                onClick={handleResendOtp}
              >
                Resend
              </span>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SignupModal;
